import {
  ONE_LETTER_WORDS,
  SUGGEST_PREFIX_MIN_LENGTH,
  type SuggestResponse,
} from "./suggest-contract";

export interface SuggestOptions {
  /** Aborts once the child has typed on or the keyboard has gone. */
  readonly signal: AbortSignal;
}

export type SuggestWord = (
  prefix: string,
  options: SuggestOptions,
) => Promise<SuggestResponse>;

export type SuggestionOutcome =
  | { readonly kind: "answered"; readonly response: SuggestResponse }
  | { readonly kind: "failed" };

/** What a suggestion answers: the word being built, in the host's text. */
export interface SuggestionQuestion {
  readonly prefix: string;
  readonly context: string | undefined;
}

export interface SuggestionAnswer extends SuggestionQuestion {
  readonly outcome: SuggestionOutcome;
}

/**
 * How a finished word was reached. "word" is known to be a word, "chip" is a
 * word the host suggested, and "unchecked" is letters nothing could check.
 */
export type CompletedVia = "word" | "chip" | "unchecked";

export interface WordChoices {
  /** How Done would finish the typed letters, or null while it cannot. */
  readonly finish: Exclude<CompletedVia, "chip"> | null;
  readonly chip: string | null;
}

const NOTHING_YET: WordChoices = { finish: null, chip: null };
const UNCHECKED: WordChoices = { finish: "unchecked", chip: null };

export const shouldAskForSuggestion = (prefix: string): boolean =>
  prefix.length >= SUGGEST_PREFIX_MIN_LENGTH;

export const answerFor = (
  answer: SuggestionAnswer | null,
  { prefix, context }: SuggestionQuestion,
): SuggestionOutcome | null =>
  answer !== null && answer.prefix === prefix && answer.context === context
    ? answer.outcome
    : null;

const choicesFromResponse = (
  { suggestion, isWord }: SuggestResponse,
  word: string,
): WordChoices => ({
  finish: isWord ? "word" : null,
  chip: suggestion !== null && suggestion !== word ? suggestion : null,
});

// The letter model has no end-of-word marker, so it never vouches for a word:
// only the one-letter set and the host's answer can. Without an answer to
// rely on, Done hands the letters over as unchecked and the host decides.
export const wordChoices = (
  question: SuggestionQuestion,
  suggesting: boolean,
  answer: SuggestionAnswer | null,
): WordChoices => {
  const word = question.prefix;
  if (!shouldAskForSuggestion(word))
    return ONE_LETTER_WORDS.has(word)
      ? { finish: "word", chip: null }
      : NOTHING_YET;
  if (!suggesting) return UNCHECKED;
  const outcome = answerFor(answer, question);
  if (outcome === null) return NOTHING_YET;
  return outcome.kind === "failed"
    ? UNCHECKED
    : choicesFromResponse(outcome.response, word);
};
